import type { DefaultRootState } from 'react-redux';

import type { Nullable } from 'types';
import { SourceConfigDto } from 'utils/campaign/requirementUtils/types';
import {
    CAMPAIGN_MAX_AGE,
    CAMPAIGN_MIN_AGE,
    CampaignDeviceType,
    CampaignGender,
    CampaignLanguage,
    CampaignProcess,
    CampaignSource,
    CampaignSteps,
    CampaignSubSource,
    CampaignSubSourceDisableReason,
    CampaignType,
} from 'utils/constants/campaign';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export interface SalaryRange {
    min: Nullable<number>;
    max: Nullable<number>;
}

type CurrentStep = number;
type AdCampaignTitle = string;
type AdRegions = string[];
type AdProfessions = string[];
type AdAgeFrom = number;
type AdAgeTo = number;
type AdUnknownAge = boolean;
type AdTotalBudget = Nullable<string>;
type AdDailyBudget = Nullable<string>;
type CampaignVacancyId = number | null;
type CampaignId = number | null;
type CampaignHash = string;
type AdAutoSource = boolean;
type BudgetLimitsConfig = Partial<Record<CampaignSubSource, BudgetLimit>>;
type Bid = number | null;
export type PricePerClick = number;
export const enum PriceType {
    Dynamic = 'DYNAMIC',
    Fixed = 'FIXED',
}

export interface CampaignErrors {
    areas: string;
    adProfessions: string;
    adGender: string;
    adAgeFrom: string;
    adAgeTo: string;
    adSalaryMin: string;
    adSalaryMax: string;
    adSalaryCommon: string;
    adSource: string;
    adCampaignTitle: string;
    campaignId: string;
    adTotalBudget: string;
    adTotalBudgetNoLimits: string;
    adDailyBudget: string;
}

interface BudgetLimit {
    min: number;
    recommended: number;
}

export interface Age {
    adAgeFrom: AdAgeFrom;
    adAgeTo: AdAgeTo;
}

interface SubSource {
    subSource: CampaignSubSource;
    enabled: boolean;
    disableReason: CampaignSubSourceDisableReason | null;
}

export interface Source {
    apiSource: CampaignSource;
    subSources: SubSource[];
}

// ----------------------- actions -------------------------------------
const SET_CAMPAIGN_FORM_STEP = 'SET_CAMPAIGN_FORM_STEP';
const SET_CAMPAIGN_TITLE = 'SET_CAMPAIGN_TITLE';
const SET_CAMPAIGN_ID_FOR_EDIT = 'SET_CAMPAIGN_ID_FOR_EDIT';
const SET_CAMPAIGN_REGIONS = 'SET_CAMPAIGN_REGIONS';
const SET_CAMPAIGN_EXCLUDED_REGIONS = 'SET_CAMPAIGN_EXCLUDED_REGIONS';
const SET_CAMPAIGN_PROFESSIONS = 'SET_CAMPAIGN_PROFESSIONS';
const SET_CAMPAIGN_GENDER = 'SET_CAMPAIGN_GENDER';
const SET_CAMPAIGN_AGE = 'SET_CAMPAIGN_AGE';
const SET_CAMPAIGN_UNKNOWN_AGE = 'SET_CAMPAIGN_UNKNOWN_AGE';
const SET_CAMPAIGN_SALARY = 'SET_CAMPAIGN_SALARY';
const SET_CAMPAIGN_LANGUAGE = 'SET_CAMPAIGN_LANGUAGE';
const SET_CAMPAIGN_SOURCE = 'SET_CAMPAIGN_SOURCE';
const SET_CAMPAIGN_TOTAL_BUDGET = 'SET_CAMPAIGN_TOTAL_BUDGET';
const SET_CAMPAIGN_TOTAL_BUDGET_NO_LIMITS = 'SET_CAMPAIGN_TOTAL_BUDGET_NO_LIMITS';
const SET_CAMPAIGN_DAILY_BUDGET = 'SET_CAMPAIGN_DAILY_BUDGET';
const SET_IS_SHOWN_DAILY_BUDGET = 'SET_IS_SHOWN_DAILY_BUDGET';
const SET_CAMPAIGN_PRICE_PER_CLICK = 'SET_CAMPAIGN_PRICE_PER_CLICK';
const SET_CAMPAIGN_PRICE_TYPE = 'SET_CAMPAIGN_PRICE_TYPE';
const SET_CAMPAIGN_ERRORS = 'SET_CAMPAIGN_ERRORS';
const SET_CAMPAIGN_VACANCY_ID = 'SET_CAMPAIGN_VACANCY_ID';
const SET_CAMPAIGN_HASH = 'SET_CAMPAIGN_HASH';
const SET_CAMPAIGN_AUTO_SOURCE = 'SET_CAMPAIGN_AUTO_SOURCE';
const SET_CAMPAIGN_ALL_SOURCES = 'SET_CAMPAIGN_ALL_SOURCES';
const SET_SOURCES_CONFIG = 'SET_SOURCES_CONFIG';
const SET_CAMPAIGN_SAVED_SOURCES = 'SET_CAMPAIGN_SAVED_SOURCES';
const SET_CAMPAIGN_SAVED_DEVICE_TYPE = 'SET_CAMPAIGN_SAVED_DEVICE_TYPE';
const SET_CAMPAIGN_BUDGET_LIMITS_CONFIG = 'SET_CAMPAIGN_BUDGET_LIMITS_CONFIG';
const SET_CAMPAIGN_TYPE = 'SET_CAMPAIGN_TYPE';
const SET_CAMPAIGN_PROCESS = 'SET_CAMPAIGN_PROCESS';
const SET_CAMPAIGN_BID = 'SET_CAMPAIGN_BID';
const SET_BANNED_CAMPAIGN_ID = 'SET_BANNED_CAMPAIGN_ID';
const RESET_CAMPAIGN_INFO = 'RESET_CAMPAIGN_INFO';
const SET_IS_ROOT_REGION_SELECTED = 'SET_IS_ROOT_REGION_SELECTED';

interface PayloadTypes {
    [SET_CAMPAIGN_FORM_STEP]: CurrentStep;
    [SET_CAMPAIGN_TITLE]: AdCampaignTitle;
    [SET_CAMPAIGN_ID_FOR_EDIT]: CampaignId;
    [SET_CAMPAIGN_REGIONS]: AdRegions;
    [SET_CAMPAIGN_EXCLUDED_REGIONS]: AdRegions;
    [SET_CAMPAIGN_PROFESSIONS]: AdProfessions;
    [SET_CAMPAIGN_GENDER]: CampaignGender;
    [SET_CAMPAIGN_AGE]: Age;
    [SET_CAMPAIGN_UNKNOWN_AGE]: AdUnknownAge;
    [SET_CAMPAIGN_SALARY]: Nullable<SalaryRange>;
    [SET_CAMPAIGN_LANGUAGE]: CampaignLanguage[];
    [SET_CAMPAIGN_SOURCE]: CampaignSubSource[];
    [SET_CAMPAIGN_TOTAL_BUDGET]: AdTotalBudget;
    [SET_CAMPAIGN_TOTAL_BUDGET_NO_LIMITS]: boolean;
    [SET_CAMPAIGN_DAILY_BUDGET]: AdDailyBudget;
    [SET_IS_SHOWN_DAILY_BUDGET]: boolean;
    [SET_CAMPAIGN_PRICE_PER_CLICK]: PricePerClick;
    [SET_CAMPAIGN_PRICE_TYPE]: PriceType;
    [SET_CAMPAIGN_ERRORS]: Partial<CampaignErrors>;
    [SET_CAMPAIGN_VACANCY_ID]: CampaignVacancyId;
    [SET_CAMPAIGN_HASH]: CampaignHash;
    [SET_CAMPAIGN_AUTO_SOURCE]: AdAutoSource;
    [SET_CAMPAIGN_ALL_SOURCES]: boolean;
    [SET_SOURCES_CONFIG]: SourceConfigDto[];
    [SET_CAMPAIGN_SAVED_SOURCES]: CampaignSubSource[];
    [SET_CAMPAIGN_SAVED_DEVICE_TYPE]: CampaignDeviceType;
    [SET_CAMPAIGN_BUDGET_LIMITS_CONFIG]: BudgetLimitsConfig;
    [SET_CAMPAIGN_TYPE]: CampaignType | null;
    [SET_CAMPAIGN_PROCESS]: CampaignProcess;
    [SET_CAMPAIGN_BID]: Bid;
    [SET_BANNED_CAMPAIGN_ID]: number | null;
    [RESET_CAMPAIGN_INFO]: null;
    [SET_IS_ROOT_REGION_SELECTED]: boolean;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaignFormStep = ActionCreator(SET_CAMPAIGN_FORM_STEP);
export const setCampaignTitle = ActionCreator(SET_CAMPAIGN_TITLE);
export const setCampaignId = ActionCreator(SET_CAMPAIGN_ID_FOR_EDIT);
export const setCampaignRegions = ActionCreator(SET_CAMPAIGN_REGIONS);
export const setCampaignExcludedRegions = ActionCreator(SET_CAMPAIGN_EXCLUDED_REGIONS);
export const setCampaignProfessions = ActionCreator(SET_CAMPAIGN_PROFESSIONS);
export const setCampaignGender = ActionCreator(SET_CAMPAIGN_GENDER);
export const setCampaignAge = ActionCreator(SET_CAMPAIGN_AGE);
export const setCampaignUnknownAge = ActionCreator(SET_CAMPAIGN_UNKNOWN_AGE);
export const setCampaignSalary = ActionCreator(SET_CAMPAIGN_SALARY);
export const setCampaignLanguage = ActionCreator(SET_CAMPAIGN_LANGUAGE);
export const setCampaignSubSource = ActionCreator(SET_CAMPAIGN_SOURCE);
export const setCampaignTotalBudget = ActionCreator(SET_CAMPAIGN_TOTAL_BUDGET);
export const setCampaignTotalBudgetNoLimits = ActionCreator(SET_CAMPAIGN_TOTAL_BUDGET_NO_LIMITS);
export const setCampaignDailyBudget = ActionCreator(SET_CAMPAIGN_DAILY_BUDGET);
export const setIsShownDailyBudget = ActionCreator(SET_IS_SHOWN_DAILY_BUDGET);
export const setCampaignPricePerClick = ActionCreator(SET_CAMPAIGN_PRICE_PER_CLICK);
export const setCampaignPriceType = ActionCreator(SET_CAMPAIGN_PRICE_TYPE);
export const setCampaignErrors = ActionCreator(SET_CAMPAIGN_ERRORS);
export const setCampaignVacancyId = ActionCreator(SET_CAMPAIGN_VACANCY_ID);
export const setCampaignHash = ActionCreator(SET_CAMPAIGN_HASH);
export const setCampaignAutoSource = ActionCreator(SET_CAMPAIGN_AUTO_SOURCE);
export const setCampaignAllSources = ActionCreator(SET_CAMPAIGN_ALL_SOURCES);
export const setSourcesConfig = ActionCreator(SET_SOURCES_CONFIG);
export const setCampaignSavedSources = ActionCreator(SET_CAMPAIGN_SAVED_SOURCES);
export const setCampaignSavedDeviceType = ActionCreator(SET_CAMPAIGN_SAVED_DEVICE_TYPE);
export const setCampaignBudgetLimitsConfig = ActionCreator(SET_CAMPAIGN_BUDGET_LIMITS_CONFIG);
export const setCampaignType = ActionCreator(SET_CAMPAIGN_TYPE);
export const setCampaignProcess = ActionCreator(SET_CAMPAIGN_PROCESS);
export const setCampaignBid = ActionCreator(SET_CAMPAIGN_BID);
export const setBannedCampaignId = ActionCreator(SET_BANNED_CAMPAIGN_ID);
export const resetCampaignInfo = ActionCreator(RESET_CAMPAIGN_INFO);
export const setIsRootRegionSelected = ActionCreator(SET_IS_ROOT_REGION_SELECTED);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
export interface CreateCampaignState {
    currentStep: CurrentStep;
    adCampaignTitle: AdCampaignTitle;
    campaignId: CampaignId;
    areas: {
        include: AdRegions;
        exclude: AdRegions;
    };
    // содержит выбранные в кампании проф. роли
    adProfessions: AdProfessions;
    adGender: CampaignGender;
    adAgeFrom: AdAgeFrom;
    adAgeTo: AdAgeTo;
    adUnknownAge: AdUnknownAge;
    adSalary: Nullable<SalaryRange>;
    adLanguage: CampaignLanguage[];
    adSource: CampaignSubSource[];
    adAllSources: boolean;
    adSourcesConfig: SourceConfigDto[];
    savedSources: CampaignSubSource[];
    savedDeviceType: CampaignDeviceType;
    pricePerClick: PricePerClick;
    priceType: PriceType;
    adTotalBudget: AdTotalBudget;
    adTotalBudgetNoLimits: boolean;
    adDailyBudget: AdDailyBudget;
    isShownDailyBudget: boolean;
    campaignErrors: Partial<CampaignErrors>;
    campaignVacancyId: CampaignVacancyId;
    campaignHash: CampaignHash;
    // поле adAutoSource больше на фронтенд не выводится, но сохраняется в стор для старых РК
    adAutoSource: AdAutoSource;
    budgetLimitsConfig: BudgetLimitsConfig;
    campaignType: CampaignType | null;
    campaignProcess: CampaignProcess;
    bid: Bid;
    bannedCampaignId: number | null;
    isRootRegionSelected: boolean;
}

export const CREATE_CAMPAIGN_INITIAL_STATE: CreateCampaignState = {
    currentStep: +CampaignSteps.First,
    adCampaignTitle: '',
    campaignId: null,
    areas: { include: [], exclude: [] },
    adProfessions: [],
    adGender: CampaignGender.Any,
    adAgeFrom: CAMPAIGN_MIN_AGE,
    adAgeTo: CAMPAIGN_MAX_AGE,
    adUnknownAge: true,
    adSalary: null,
    adLanguage: [],
    adSource: [],
    adAllSources: true,
    adSourcesConfig: [],
    savedSources: [],
    savedDeviceType: CampaignDeviceType.Any,
    pricePerClick: 0,
    priceType: PriceType.Fixed,
    adTotalBudget: '',
    adTotalBudgetNoLimits: false,
    adDailyBudget: '',
    isShownDailyBudget: false,
    campaignErrors: {},
    campaignVacancyId: null,
    campaignHash: '',
    adAutoSource: true,
    budgetLimitsConfig: {},
    campaignType: null,
    campaignProcess: CampaignProcess.Create,
    bid: null,
    bannedCampaignId: null,
    isRootRegionSelected: false,
};

export default createReducer<CreateCampaignState, PayloadTypes>(CREATE_CAMPAIGN_INITIAL_STATE, {
    [SET_CAMPAIGN_FORM_STEP]: (state, { payload }) => ({
        ...state,
        currentStep: payload,
    }),
    [SET_CAMPAIGN_HASH]: (state, { payload }) => ({
        ...state,
        campaignHash: payload,
    }),
    [SET_CAMPAIGN_REGIONS]: (state, { payload }) => ({
        ...state,
        areas: {
            ...state.areas,
            include: payload,
        },
    }),
    [SET_CAMPAIGN_EXCLUDED_REGIONS]: (state, { payload }) => ({
        ...state,
        areas: {
            ...state.areas,
            exclude: payload,
        },
    }),
    [SET_CAMPAIGN_TITLE]: (state, { payload }) => ({
        ...state,
        adCampaignTitle: payload,
    }),
    [SET_CAMPAIGN_ID_FOR_EDIT]: (state, { payload }) => ({
        ...state,
        campaignId: payload,
    }),
    [SET_CAMPAIGN_PROFESSIONS]: (state, { payload }) => ({
        ...state,
        adProfessions: payload,
    }),
    [SET_CAMPAIGN_GENDER]: (state, { payload }) => ({
        ...state,
        adGender: payload,
    }),
    [SET_CAMPAIGN_AGE]: (state, { payload }) => ({
        ...state,
        adAgeFrom: payload.adAgeFrom,
        adAgeTo: payload.adAgeTo,
    }),
    [SET_CAMPAIGN_UNKNOWN_AGE]: (state, { payload }) => ({
        ...state,
        adUnknownAge: payload,
    }),
    [SET_CAMPAIGN_SALARY]: (state, { payload }) => ({
        ...state,
        adSalary: payload,
    }),
    [SET_CAMPAIGN_LANGUAGE]: (state, { payload }) => ({
        ...state,
        adLanguage: payload,
    }),
    [SET_CAMPAIGN_SOURCE]: (state, { payload }) => ({
        ...state,
        adSource: payload,
    }),
    [SET_CAMPAIGN_TOTAL_BUDGET]: (state, { payload }) => ({
        ...state,
        adTotalBudget: payload,
    }),
    [SET_CAMPAIGN_TOTAL_BUDGET_NO_LIMITS]: (state, { payload }) => ({
        ...state,
        adTotalBudgetNoLimits: payload,
    }),
    [SET_CAMPAIGN_DAILY_BUDGET]: (state, { payload }) => ({
        ...state,
        adDailyBudget: payload,
    }),
    [SET_IS_SHOWN_DAILY_BUDGET]: (state, { payload }) => ({
        ...state,
        isShownDailyBudget: payload,
    }),
    [SET_CAMPAIGN_PRICE_PER_CLICK]: (state, { payload }) => ({
        ...state,
        pricePerClick: payload,
    }),
    [SET_CAMPAIGN_PRICE_TYPE]: (state, { payload }) => ({
        ...state,
        priceType: payload,
    }),
    [SET_CAMPAIGN_ERRORS]: (state, { payload }) => ({
        ...state,
        campaignErrors: payload,
    }),
    [SET_CAMPAIGN_VACANCY_ID]: (state, { payload }) => ({
        ...state,
        campaignVacancyId: payload,
    }),
    [SET_CAMPAIGN_AUTO_SOURCE]: (state, { payload }) => ({
        ...state,
        adAutoSource: payload,
    }),
    [SET_CAMPAIGN_ALL_SOURCES]: (state, { payload }) => ({
        ...state,
        adAllSources: payload,
    }),
    [SET_SOURCES_CONFIG]: (state, { payload }) => ({
        ...state,
        adSourcesConfig: payload,
    }),
    [SET_CAMPAIGN_SAVED_SOURCES]: (state, { payload }) => ({
        ...state,
        savedSources: payload,
    }),
    [SET_CAMPAIGN_SAVED_DEVICE_TYPE]: (state, { payload }) => ({
        ...state,
        savedDeviceType: payload,
    }),
    [SET_CAMPAIGN_BUDGET_LIMITS_CONFIG]: (state, { payload }) => ({
        ...state,
        budgetLimitsConfig: payload,
    }),
    [SET_CAMPAIGN_TYPE]: (state, { payload }) => ({
        ...state,
        campaignType: payload,
    }),
    [SET_CAMPAIGN_PROCESS]: (state, { payload }) => ({
        ...state,
        campaignProcess: payload,
    }),
    [SET_CAMPAIGN_BID]: (state, { payload }) => ({
        ...state,
        bid: payload,
    }),
    [SET_BANNED_CAMPAIGN_ID]: (state, { payload }) => ({
        ...state,
        bannedCampaignId: payload,
    }),
    [RESET_CAMPAIGN_INFO]: (state) => ({
        ...state,
        ...CREATE_CAMPAIGN_INITIAL_STATE,
    }),
    [SET_IS_ROOT_REGION_SELECTED]: (state, { payload }) => ({
        ...state,
        isRootRegionSelected: payload,
    }),
});
// ----------------------- /reducer --------------------------------------

// ----------------------- selectors -------------------------------------
export const selectCreateCampaignState = (state: DefaultRootState): CreateCampaignState => state.createCampaign;

export const selectCampaignProcess = (state: DefaultRootState): CreateCampaignState['campaignProcess'] =>
    selectCreateCampaignState(state).campaignProcess;

export const selectCampaignAreas = (state: DefaultRootState): CreateCampaignState['areas'] =>
    selectCreateCampaignState(state).areas;

export const selectCampaignRegions = (state: DefaultRootState): CreateCampaignState['areas']['include'] =>
    selectCampaignAreas(state).include;

export const selectCampaignExcludedRegions = (state: DefaultRootState): CreateCampaignState['areas']['exclude'] =>
    selectCampaignAreas(state).exclude;

export const selectIsDynamicPriceType = (state: DefaultRootState): boolean =>
    state.createCampaign.priceType === PriceType.Dynamic;

export const selectIsRootRegionSelected = (state: DefaultRootState): boolean =>
    selectCreateCampaignState(state).isRootRegionSelected;
// ----------------------- /selectors --------------------------------------
